/**
 * The load/validate/serialise logic behind `settings.svelte.ts`, pulled out
 * into a plain module on purpose: `$state` only exists once the Svelte
 * compiler has processed a file, so nothing that needs to run under plain
 * Vitest (no Svelte plugin — see `vitest.config.ts`) can live in a
 * `.svelte.ts` file. This module has no rune in it and is what
 * `test/state/settings.test.ts` actually exercises.
 */
import { DEFAULT_APPEARANCE } from '@artibens/app-shell';

import { INTERLOCK_ACCENT, parseAccent } from '../theme/accent.js';

export const SETTINGS_STORAGE_KEY = 'interlock:settings:v1';

/**
 * The chrome's appearance. Light is the default, whatever the OS prefers (the
 * house's default since shell 1.4.0, `DEFAULT_APPEARANCE`;
 * `docs/decisions/0057`), and Dark is the player's choice. A stored choice
 * always wins: a blob that names `'dark'` stays dark. A stored `'auto'` from
 * an older build is not a value any more and falls back to the default.
 */
export type Appearance = 'dark' | 'light';

export const APPEARANCE_VALUES: readonly Appearance[] = ['dark', 'light'];

export function isAppearance(value: unknown): value is Appearance {
  return typeof value === 'string' && (APPEARANCE_VALUES as readonly string[]).includes(value);
}

export interface Settings {
  dasMs: number;
  arrMs: number;
  ghost: boolean;
  highContrast: boolean;
  /** Music and sound effects: on or off, and a volume each, persisted and synced like every other setting here. */
  musicOn: boolean;
  /** 0..1. */
  musicVolume: number;
  sfxOn: boolean;
  /** 0..1. */
  sfxVolume: number;
  /**
   * The app's own chrome (every screen, menu and button outside the game
   * canvas) is one fixed design in a light and a dark variant: light unless
   * the player chooses Dark. It is one more field on the settings blob, so
   * it syncs across devices with the account; the server does not validate
   * that blob's shape (`settings: unknown`).
   */
  appearance: Appearance;
  /**
   * The app's accent colour, as the player chose it: a preset's hex or a
   * custom `#rrggbb` (`theme/accent.ts`, `docs/decisions/0056`). The
   * shell's engine resolves it for the base on show; what is stored is the
   * choice, never the engine's output. Interlock's signature blue unless
   * chosen.
   */
  accent: string;
}

export const SETTINGS_DEFAULTS: Settings = {
  dasMs: 140,
  arrMs: 28,
  ghost: true,
  highContrast: false,
  musicOn: true,
  musicVolume: 0.55, // "moderate volume" — the owner's own words.
  sfxOn: true,
  sfxVolume: 0.8,
  appearance: DEFAULT_APPEARANCE,
  accent: INTERLOCK_ACCENT,
};

function readStorage(storage: Storage | undefined): Partial<Settings> {
  if (storage === undefined) return {};
  try {
    const raw = storage.getItem(SETTINGS_STORAGE_KEY);
    if (raw === null) return {};
    const parsed: unknown = JSON.parse(raw);
    return typeof parsed === 'object' && parsed !== null ? (parsed as Partial<Settings>) : {};
  } catch {
    return {};
  }
}

/** `storage` defaults to `localStorage`, injectable so this stays testable without a real browser. */
export function loadInitialSettings(storage: Storage | undefined = safeLocalStorage()): Settings {
  return validateSettings(readStorage(storage));
}

/**
 * Every field's type check, range check and fallback, in one place.
 *
 * Split out of `loadInitialSettings` when accounts arrived: a settings blob
 * now has two possible origins — this device's `localStorage`, and the account
 * the player just signed in on — and both have to be treated with the same
 * suspicion. A blob written by a newer build, or by a different device
 * entirely, must never be able to put a screen into a state it cannot render.
 * Two copies of these rules would be two copies to drift.
 *
 * Only known fields come out. That is also the migration for `themeId`, the
 * board theme an older build stored (one of six) before the board became one
 * look that follows the appearance (`docs/decisions/0056`): it is read from
 * neither this device nor an account, and the next write leaves it out.
 */
export function validateSettings(raw: unknown): Settings {
  const stored: Partial<Settings> =
    typeof raw === 'object' && raw !== null ? (raw as Partial<Settings>) : {};
  return {
    dasMs:
      typeof stored.dasMs === 'number' && stored.dasMs > 0 ? stored.dasMs : SETTINGS_DEFAULTS.dasMs,
    arrMs:
      typeof stored.arrMs === 'number' && stored.arrMs > 0 ? stored.arrMs : SETTINGS_DEFAULTS.arrMs,
    ghost: typeof stored.ghost === 'boolean' ? stored.ghost : SETTINGS_DEFAULTS.ghost,
    highContrast:
      typeof stored.highContrast === 'boolean'
        ? stored.highContrast
        : SETTINGS_DEFAULTS.highContrast,
    musicOn: typeof stored.musicOn === 'boolean' ? stored.musicOn : SETTINGS_DEFAULTS.musicOn,
    musicVolume: clampedVolume(stored.musicVolume, SETTINGS_DEFAULTS.musicVolume),
    sfxOn: typeof stored.sfxOn === 'boolean' ? stored.sfxOn : SETTINGS_DEFAULTS.sfxOn,
    sfxVolume: clampedVolume(stored.sfxVolume, SETTINGS_DEFAULTS.sfxVolume),
    appearance: isAppearance(stored.appearance) ? stored.appearance : SETTINGS_DEFAULTS.appearance,
    // A hex colour, normalised; anything else is Interlock's blue.
    accent: parseAccent(stored.accent),
  };
}

/** A stored volume clamps into `[0, 1]` rather than being rejected outright — a blob written by a future build with a wider range should not fall back to the default, only be pulled back in range. Anything that is not a finite number falls back to `fallback`. */
function clampedVolume(value: unknown, fallback: number): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return fallback;
  return Math.min(1, Math.max(0, value));
}

export function writeSettings(
  settings: Settings,
  storage: Storage | undefined = safeLocalStorage(),
): void {
  if (storage === undefined) return;
  try {
    storage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Storage full or blocked (private browsing) — settings just stop persisting.
  }
}

/** `localStorage` does not exist before a page has loaded in some embed contexts; this never throws. */
export function safeLocalStorage(): Storage | undefined {
  try {
    return typeof localStorage === 'undefined' ? undefined : localStorage;
  } catch {
    return undefined;
  }
}
